import { yupResolver } from '@hookform/resolvers/yup';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import InvalidPermissionsDialog from '../../../../components/invalid-permissions-dialog/InvalidPermissionsDialog';
import { JOB_MANAGER_PATHS } from '../../../../constants/job-manager-paths';
import { useAuth, usePermission } from '../../../../core';
import { useTruncateChain } from '../../../../hooks';
import { ConfirmationModal } from 'components';
import { CreateJobDialog } from '../create-job-dialog';
import {
    truncateOrDropChainJobDefaults,
    TruncateOrDropChainJobFormInputs,
    TruncateOrDropChainJobSchema,
} from '../../../../pages/job-manager/job-manager-helper';
import { TruncateOrDropChainSelectField } from '../truncate-or-drop-chain-select-field/TruncateOrDropChainSelectField';
import {
    confirmationInputLabelText,
    confirmationSubHeadingText,
} from '../truncate-or-drop-chain-select-field/TruncatedChainHeading-helper';

export const CreateTruncateChainJob = (): React.JSX.Element => {
    const { mutate: truncateChain } = useTruncateChain();
    const navigate = useNavigate();
    const { hasPermissions } = usePermission();
    const { getToken } = useAuth();
    const {
        handleSubmit,
        control,
        formState: { errors },
        getValues,
    } = useForm<TruncateOrDropChainJobFormInputs>({
        mode: 'all',
        resolver: yupResolver(TruncateOrDropChainJobSchema),
        defaultValues: truncateOrDropChainJobDefaults,
    });
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [displayPermissionDialog, setDisplayPermissionDialog] =
        useState(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const openTruncationModal = (): void => {
        setModalOpen(true);
    };
    const closeTruncationModal = (): void => {
        setModalOpen(false);
    };

    const handleTruncateChainJob = (): void => {
        setIsLoading(true);
        setTimeout(() => {
            navigate(JOB_MANAGER_PATHS.LANDING_PAGE);
        }, 500);
        truncateChain(
            {
                token: getToken(),
                chain: getValues().chain,
            },
            {
                onSuccess: () => {
                    setIsLoading(false);
                },
                onError: () => {
                    setIsLoading(false);
                },
            }
        );
    };

    const onSubmit = (): void => {
        const permission = hasPermissions(['createData', 'createMetadata']);
        if (permission) {
            handleSubmit(openTruncationModal)();
        } else {
            setDisplayPermissionDialog(true);
        }
    };

    const handlePermissionDialogClose = (): void => {
        setDisplayPermissionDialog(false);
    };

    return (
        <CreateJobDialog
            handleSubmit={onSubmit}
            buttonText='TRUNCATE CHAIN'
            isLoading={isLoading}
        >
            <TruncateOrDropChainSelectField control={control} errors={errors} />
            {modalOpen && (
                <ConfirmationModal
                    confirmationModal={modalOpen}
                    handleConfirmationModal={closeTruncationModal}
                    confirmationKey={getValues().chain}
                    confirmationButtonText={'Truncate Chain'}
                    confirmationHeadingText={'Confirm Chain Truncation'}
                    isRequiredIconVisible={false}
                    confirmationInputLabelText={confirmationInputLabelText(
                        getValues().chain,
                        45
                    )}
                    handleConfirmation={handleTruncateChainJob}
                    confirmationSubHeadingText={confirmationSubHeadingText(
                        getValues().chain,
                        64
                    )}
                />
            )}
            {displayPermissionDialog && (
                <InvalidPermissionsDialog
                    open={displayPermissionDialog}
                    onClose={handlePermissionDialogClose}
                />
            )}
        </CreateJobDialog>
    );
};
